import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Send, Sparkles, ShieldAlert, Trash2,
  Star, StarOff, Camera, Image as ImageIcon, X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import Avatar from "@/components/Avatar";

function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export default function GroupDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [group, setGroup] = useState(null);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  // --- Post composer state ---
  const postFileRef = useRef(null);
  const [postText, setPostText] = useState("");
  const [postImagePath, setPostImagePath] = useState("");
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [postUploading, setPostUploading] = useState(false);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [postErr, setPostErr] = useState("");

  const loadGroup = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${id}`);
      setGroup(r.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [id]);

  const loadPosts = useCallback(async () => {
    if (!group) return;
    if (!group.joined && user?.role !== "admin") {
      setPosts([]);
      return;
    }
    try {
      const r = await api.get(`/groups/${id}/posts`);
      setPosts(r.data);
    } catch (e) { /* ignore */ }
  }, [id, group, user?.role]);

  const loadBroadcasts = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${id}/broadcasts`);
      setBroadcasts(r.data);
    } catch (e) { /* ignore */ }
  }, [id]);

  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get(`/groups/${id}/members`);
      setMembers(r.data);
    } catch (e) { /* ignore */ }
  }, [id]);

  const loadMessages = useCallback(async () => {
    if (!group?.joined && user?.role !== "admin") return;
    try {
      const r = await api.get(`/groups/${id}/messages`);
      setMessages(r.data);
    } catch (e) { /* ignore */ }
  }, [id, group?.joined, user?.role]);

  useEffect(() => {
    loadGroup();
    loadBroadcasts();
    loadMembers();
  }, [loadGroup, loadBroadcasts, loadMembers]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (tab !== "chat") return undefined;
    loadMessages();
    const t = setInterval(loadMessages, 3000);
    return () => clearInterval(t);
  }, [tab, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const join = async () => {
    try {
      await api.post(`/groups/${id}/join`);
      await loadGroup();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this group?")) return;
    try {
      await api.post(`/groups/${id}/leave`);
      await loadGroup();
      setMessages([]);
      setPosts([]);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const banUntil = user?.chat_banned_until ? new Date(user.chat_banned_until) : null;
  const isBanned = banUntil && banUntil.getTime() > Date.now();

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setErr("");
    try {
      await api.post(`/groups/${id}/messages`, { text });
      setText("");
      loadMessages();
    } catch (e2) {
      setErr(formatApiError(e2));
      if (e2?.response?.status === 403) refresh();
    }
  };

  const isSuperAdmin = user?.role === "admin";
  const isGroupMod = isSuperAdmin || (group?.moderator_ids || []).includes(user?.id);

  const deleteMessage = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/groups/${id}/messages/${msgId}`);
      loadMessages();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // --- Group post handlers ---
  const pickPostImage = () => postFileRef.current?.click();

  const handlePostImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPostErr("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setPostErr("Image too large (max 8MB).");
      return;
    }
    setPostErr("");
    setPostImagePreview(URL.createObjectURL(file));
    setPostUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      setPostImagePath(up.data.path);
    } catch (e2) {
      setPostErr(formatApiError(e2));
      setPostImagePreview(null);
    } finally {
      setPostUploading(false);
    }
  };

  const clearPostImage = () => {
    setPostImagePath("");
    setPostImagePreview(null);
  };

  const submitPost = async (e) => {
    e?.preventDefault?.();
    if (!postText.trim() && !postImagePath) return;
    setPostErr("");
    setPostSubmitting(true);
    try {
      await api.post(`/groups/${id}/posts`, {
        text: postText.trim(),
        image_path: postImagePath || null,
      });
      setPostText("");
      setPostImagePath("");
      setPostImagePreview(null);
      await loadPosts();
    } catch (e2) {
      setPostErr(formatApiError(e2));
      if (e2?.response?.status === 403) refresh();
    } finally {
      setPostSubmitting(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/groups/${id}/posts/${postId}`);
      await loadPosts();
    } catch (e2) {
      setErr(formatApiError(e2));
    }
  };

  const toggleModerator = async (m) => {
    try {
      if (m.is_group_moderator) {
        if (!window.confirm(`Remove ${m.name} as moderator?`)) return;
        await api.delete(`/groups/${id}/moderators/${m.id}`);
      } else {
        if (!window.confirm(`Make ${m.name} a moderator of this group?`)) return;
        await api.post(`/groups/${id}/moderators/${m.id}`);
      }
      await loadGroup();
      await loadMembers();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // Cover photo upload (super-admin or per-group moderator only).
  const coverInputRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const onPickCover = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setErr("Only image files are allowed.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      setErr("Image too large (max 8MB).");
      return;
    }
    setErr("");
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/upload", fd);
      const r = await api.patch(`/groups/${id}`, { image_path: up.data.path });
      setGroup(r.data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setCoverUploading(false);
    }
  };

  if (!group) {
    return (
      <div className="py-12 text-center text-white/40 text-sm tracking-widest uppercase" data-testid="group-loading">
        Loading…
      </div>
    );
  }

  const canChat = group.joined || user?.role === "admin";
  const canPost = group.joined || user?.role === "admin";
  const canModerate = user?.role === "admin" || (group.moderator_ids || []).includes(user?.id);

  return (
    <div className="space-y-6 fade-up">
      <button onClick={() => nav("/groups")} className="text-white/60 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1" data-testid="group-back">
        <ArrowLeft size={14} strokeWidth={1.5} /> All Groups
      </button>

      {/* Header with cover image */}
      <div className="border border-white/10 rounded-md overflow-hidden bg-[#141414]">
        <div className="relative h-40 sm:h-56 bg-black">
          {group.image_path ? (
            <AuthImage path={group.image_path} className="w-full h-full object-cover opacity-90" data-testid="group-cover-image" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
              <Sparkles size={32} strokeWidth={1} className="text-[#d4af37]/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
          {canModerate && (
            <>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickCover}
                data-testid="group-cover-input"
              />
              <button
                onClick={() => coverInputRef.current?.click()}
                disabled={coverUploading}
                className="absolute top-3 right-3 bg-black/60 border border-white/20 hover:border-[#d4af37]/60 backdrop-blur text-xs uppercase tracking-widest text-white/80 hover:text-white px-3 py-1.5 flex items-center gap-2 disabled:opacity-60"
                data-testid="group-cover-change"
                title="Change cover photo"
              >
                <Camera size={12} strokeWidth={1.5} />
                {coverUploading ? "Uploading…" : group.image_path ? "Change cover" : "Add cover"}
              </button>
            </>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[#d4af37] text-xs uppercase tracking-[0.3em]">Group</p>
              <h1 className="font-serif text-3xl sm:text-4xl mt-1">{group.name}</h1>
              <p className="text-white/60 mt-2 text-sm">{group.description}</p>
              <div className="flex items-center gap-2 mt-3 text-[10px] uppercase tracking-widest text-white/40">
                <Users size={12} strokeWidth={1.5} /> {group.member_count} members
                {group.is_official && <span className="text-[#d4af37]">· Official</span>}
              </div>
            </div>
            {group.joined ? (
              <button
                onClick={leave}
                className="shrink-0 border border-white/15 hover:border-red-400/40 hover:text-red-300 px-3 py-2 text-[10px] uppercase tracking-widest text-white/60"
                data-testid="group-leave"
              >
                Leave
              </button>
            ) : (
              <button
                onClick={join}
                className="shrink-0 bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab]"
                data-testid="group-join"
              >
                Join
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
        {[
          ["posts", `Posts · ${posts.length}`],
          ["chat", "Chat"],
          ["broadcasts", `Concierge · ${broadcasts.length}`],
          ["members", `Members · ${members.length}`],
        ].map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-3 text-xs uppercase tracking-[0.2em] -mb-px border-b whitespace-nowrap ${
              tab === t ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-white/50"
            }`}
            data-testid={`group-tab-${t}`}
          >
            {label}
          </button>
        ))}
      </div>

      {err && <p className="text-red-400 text-sm" data-testid="group-error">{err}</p>}

      {/* POSTS TAB */}
      {tab === "posts" && (
        <div className="space-y-4" data-testid="group-posts">
          {!canPost ? (
            <div className="border border-white/10 rounded-md p-8 text-center text-white/50 text-sm" data-testid="group-posts-locked">
              Join this group to see and create posts.
            </div>
          ) : (
            <>
              {/* Composer (members only) */}
              <form onSubmit={submitPost} className="border border-white/10 rounded-md p-4 bg-[#141414]" data-testid="group-post-composer">
                {isBanned && (
                  <div className="mb-3 rounded-md border border-red-500/40 bg-red-950/20 p-3 text-xs text-red-200 flex gap-2">
                    <ShieldAlert size={14} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />
                    <span>您因违反规定已被禁言至 {banUntil.toLocaleString()}.</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <Avatar path={user?.avatar_path} name={user?.name} size={36} className="shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <textarea
                      value={postText}
                      onChange={(e) => setPostText(e.target.value)}
                      rows={2}
                      maxLength={2000}
                      placeholder={`Share something with ${group.name}…`}
                      disabled={isBanned || postSubmitting}
                      className="w-full bg-transparent border-b border-white/10 focus:border-[#d4af37] outline-none py-2 px-1 resize-none disabled:opacity-40"
                      data-testid="group-post-text"
                    />
                    {postImagePreview && (
                      <div className="relative mt-3 inline-block">
                        <img src={postImagePreview} alt="preview" className="max-h-64 rounded-md border border-white/10" />
                        <button
                          type="button"
                          onClick={clearPostImage}
                          className="absolute top-2 right-2 w-7 h-7 bg-black/70 rounded-full flex items-center justify-center text-white"
                          data-testid="group-post-clear-image"
                        >
                          <X size={14} strokeWidth={1.5} />
                        </button>
                        {postUploading && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-[#d4af37] uppercase tracking-widest text-xs">
                            Uploading…
                          </div>
                        )}
                      </div>
                    )}
                    {postErr && <p className="mt-2 text-red-400 text-xs" data-testid="group-post-error">{postErr}</p>}
                    <div className="flex items-center justify-between mt-3 gap-2">
                      <input ref={postFileRef} type="file" accept="image/*" className="hidden" onChange={handlePostImage} data-testid="group-post-file-input" />
                      <button
                        type="button"
                        onClick={pickPostImage}
                        disabled={isBanned || postUploading}
                        className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-[#d4af37] disabled:opacity-40"
                        data-testid="group-post-pick-image"
                      >
                        <ImageIcon size={14} strokeWidth={1.5} /> Photo
                      </button>
                      <button
                        type="submit"
                        disabled={
                          isBanned || postSubmitting || postUploading ||
                          (!postText.trim() && !postImagePath)
                        }
                        className="bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#f3e5ab] disabled:opacity-30 flex items-center gap-2"
                        data-testid="group-post-submit"
                      >
                        <Send size={14} strokeWidth={2} />
                        {postSubmitting ? "Posting…" : "Post"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* List */}
              {posts.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-8" data-testid="group-posts-empty">
                  No posts yet. Be the first to share something.
                </p>
              ) : (
                posts.map((p) => {
                  const canDelete = p.author_id === user?.id || isGroupMod;
                  return (
                    <article
                      key={p.id}
                      className="border border-white/10 rounded-md p-4 bg-[#141414] group"
                      data-testid={`group-post-${p.id}`}
                    >
                      <header className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar path={p.author?.avatar_path} name={p.author?.name} size={36} className="shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm flex items-center gap-2 flex-wrap">
                              <span className="font-medium truncate">{p.author?.name || p.author_name}</span>
                              {p.author?.role === "admin" && (
                                <span className="text-[10px] uppercase tracking-widest text-[#d4af37]">Concierge</span>
                              )}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-white/40">{relTime(p.created_at)}</div>
                          </div>
                        </div>
                        {canDelete && (
                          <button
                            onClick={() => deletePost(p.id)}
                            className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400 transition shrink-0"
                            title="Delete post"
                            data-testid={`group-post-delete-${p.id}`}
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        )}
                      </header>
                      {p.text && (
                        <p className="mt-3 text-sm whitespace-pre-wrap break-words">{p.text}</p>
                      )}
                      {p.image_path && (
                        <div className="mt-3 rounded-md overflow-hidden border border-white/10 bg-black">
                          <AuthImage path={p.image_path} className="w-full max-h-[70vh] object-contain" />
                        </div>
                      )}
                    </article>
                  );
                })
              )}
            </>
          )}
        </div>
      )}

      {/* CHAT TAB */}
      {tab === "chat" && (
        <div className="border border-white/10 rounded-md flex flex-col min-h-[55vh]">
          {!canChat ? (
            <div className="flex-1 flex items-center justify-center text-white/50 text-sm p-8 text-center" data-testid="group-chat-locked">
              Join this group to read and post in the chat.
            </div>
          ) : (
            <>
              {isBanned && (
                <div className="m-3 rounded-md border border-red-500/40 bg-red-950/20 p-3 text-xs text-red-200 flex gap-2" data-testid="group-ban-banner">
                  <ShieldAlert size={14} strokeWidth={1.5} className="text-red-400 shrink-0 mt-0.5" />
                  <span>您因违反规定已被禁言至 {banUntil.toLocaleString()}.</span>
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4 space-y-2" ref={scrollRef} data-testid="group-thread">
                {messages.length === 0 ? (
                  <p className="text-white/40 text-sm text-center mt-12">Say hello to the group.</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    const isAdmin = m.sender_role === "admin";
                    return (
                      <div key={m.id} className={`flex group ${mine ? "justify-end" : "justify-start"}`} data-testid={`group-msg-${m.id}`}>
                        <div className="flex items-start gap-2 max-w-[80%]">
                          {!mine && <Avatar name={m.sender_name} size={28} className="mt-1 shrink-0" />}
                          <div
                            className={`px-3 py-2 rounded-2xl text-sm ${
                              mine
                                ? "bg-[#d4af37] text-black rounded-br-sm"
                                : isAdmin
                                ? "bg-[#0f5132] border border-[#d4af37]/40 text-white/95 rounded-bl-sm"
                                : "bg-[#1a1a1a] border border-white/10 text-white/90 rounded-bl-sm"
                            }`}
                          >
                            {!mine && (
                              <div className={`text-[10px] uppercase tracking-widest mb-0.5 ${isAdmin ? "text-[#d4af37]" : "text-white/50"}`}>
                                {m.sender_name} {isAdmin && "· Concierge"}
                              </div>
                            )}
                            <div>{m.text}</div>
                            <div className={`text-[10px] mt-1 ${mine ? "text-black/50" : "text-white/40"}`}>{timeLabel(m.created_at)}</div>
                          </div>
                          {(mine || isGroupMod) && (
                            <button
                              onClick={() => deleteMessage(m.id)}
                              className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-red-400 transition shrink-0 mt-2"
                              title="Delete message"
                              data-testid={`group-msg-delete-${m.id}`}
                            >
                              <Trash2 size={12} strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <form onSubmit={sendMessage} className="p-3 border-t border-white/10 flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={isBanned ? "您当前已被禁言…" : "Message the group…"}
                  disabled={isBanned}
                  enterKeyHint="send"
                  className="flex-1 bg-transparent border-b border-white/10 focus:border-[#d4af37] outline-none py-2 px-1 disabled:opacity-40"
                  data-testid="group-msg-input"
                />
                <button
                  type="submit"
                  disabled={!text.trim() || isBanned}
                  className="bg-[#d4af37] text-black px-4 disabled:opacity-30 flex items-center gap-2 text-xs uppercase tracking-widest"
                  data-testid="group-msg-send"
                >
                  <Send size={14} strokeWidth={2} /> Send
                </button>
              </form>
            </>
          )}
        </div>
      )}

      {/* BROADCASTS TAB */}
      {tab === "broadcasts" && (
        <div className="space-y-3" data-testid="group-broadcasts">
          {broadcasts.length === 0 ? (
            <p className="text-white/40 text-sm">No Concierge messages in this group yet.</p>
          ) : (
            broadcasts.map((b) => (
              <div key={b.id} className="border border-[#d4af37]/30 bg-gradient-to-br from-[#d4af37]/5 to-transparent p-4 rounded-md">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                  <Sparkles size={12} strokeWidth={1.5} /> {b.type}
                </div>
                <h3 className="font-serif text-xl mt-1">{b.title}</h3>
                <p className="text-white/70 text-sm mt-2">{b.body}</p>
                {b.cta_url && (
                  <a href={b.cta_url} target="_blank" rel="noreferrer" className="inline-block mt-2 text-[#d4af37] text-xs uppercase tracking-widest">
                    {b.cta_label || "Open"} →
                  </a>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* MEMBERS TAB */}
      {tab === "members" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="group-members">
          {members.length === 0 ? (
            <p className="text-white/40 col-span-full">No members yet.</p>
          ) : (
            members.map((m) => (
              <div key={m.id} className="border border-white/10 rounded-md p-3 bg-[#141414] flex items-center gap-3">
                <Avatar path={m.avatar_path} name={m.name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium truncate">{m.name}</div>
                    {m.role === "admin" && <span className="text-[10px] uppercase tracking-widest text-[#d4af37]">Concierge</span>}
                    {m.is_group_moderator && (
                      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300">
                        <Star size={10} strokeWidth={1.5} /> Moderator
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5 truncate">{m.occupation || "—"} · {m.region || "—"}</div>
                </div>
                {isSuperAdmin && m.role !== "admin" && (
                  <button
                    onClick={() => toggleModerator(m)}
                    className={`shrink-0 p-2 border border-white/10 hover:border-emerald-400/50 transition ${
                      m.is_group_moderator ? "text-emerald-300" : "text-white/60 hover:text-emerald-300"
                    }`}
                    title={m.is_group_moderator ? "Remove moderator" : "Promote to moderator"}
                    data-testid={`group-mod-toggle-${m.id}`}
                  >
                    {m.is_group_moderator ? <StarOff size={14} strokeWidth={1.5} /> : <Star size={14} strokeWidth={1.5} />}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
